/**
 * FeatureTeaser — generic upsell view for gated features (M-UX-G2, Growth-Audit
 * Befund #4).
 *
 * Renders IN PLACE of a bare `return null` whenever a monetarisierungs-gated
 * feature is generally rolled out (env flag on) but the current user does not
 * (yet) have access. Communicates what the feature does + a clear upgrade path
 * instead of leaving the user with an empty page — "a feature nobody sees can
 * never be desired" (ux-audit-growth.md Befund #4).
 *
 * Purely presentational: no Firestore reads, no network calls, no Hooks beyond
 * `useTranslation`/`useNavigate` — safe to mount for any user regardless of
 * auth/scope state (M-UX-G2 acceptance: "kein Firestore-Read im
 * Teaser-Zustand").
 *
 * Pattern reference: generalizes the pre-existing `ReceivedInvoicesPage`
 * premium lock card into a reusable component built on `components/common/`
 * (Card/Button/Badge, architecture-ux-restructure.md §4.3) so future gated
 * features (e.g. M-UX-G3 Ultra-Tier) reuse the same visual language instead of
 * re-copying markup.
 *
 * The CTA is rendered as a `common/Button` (not a raw `<Link>`) and navigates
 * via `useNavigate` on click — `common/Button` always renders a native
 * `<button>`, so wrapping it in an anchor would nest interactive elements.
 *
 * Nudge-Telemetrie (M-N5, architecture-nudge.md §7.4/§13 Delta 1): jeder
 * Consumer MUSS eine `promotionId` verdrahten (Pflicht-Prop — der Compiler
 * erzwingt die Messung jedes heutigen und künftigen Consumers). Mount feuert
 * `view_promotion`, der CTA-Klick `select_promotion`; die Navigation nutzt
 * `buildUpgradeHref(promotionId)`, das sowohl den `?ref=`-Parameter für die
 * M-N4-Checkout-Attribution als auch den `?tier=`-Parameter für die
 * Spalten-Hervorhebung auf `/dashboard/abonnement` trägt.
 *
 * `ctaTo` ENTFERNT (D-1, 2026-07-29): das Prop war seit M-N5 wirkungslos — die
 * Navigation lief bereits ausschließlich über `buildUpgradeHref`, der Rohwert
 * wurde nirgends gelesen. Sieben Consumer pflegten damit einen String
 * (`/dashboard/abonnement?tier=ultra`), der nie irgendwo ankam. Der Ziel-Tarif
 * lebt jetzt an EINER Stelle: `NUDGE_PROMOTION_TARGET_TIER` in
 * `config/nudgePromotions.ts`, abgeleitet aus der `promotionId`.
 *
 * Layout (PR-T6-F1, Reviewer-Fund F1): der Default `layout="page"` bleibt
 * das ganzseitige `min-h-screen`-Verhalten — das ist korrekt für Consumer,
 * bei denen der Teaser die GESAMTE Seite ersetzt (Bank/DATEV/OpenItems).
 * `layout="inline"` rendert dieselben Inhalte (Badge/Titel/Beschreibung/
 * Highlights/CTA, identische GA4-Anker) in einem kompakten horizontalen
 * Card-Band ohne Vollhöhe — für Sonderfälle, in denen der Teaser NUR einen
 * Teilbereich der Seite ersetzt (z. B. `ExpensesPage`: nur die
 * `ExpenseSummaryBar`-Position, die darunterliegende Beleg-Liste bleibt
 * Primärinhalt und darf nicht unter den Fold geschoben werden).
 *
 * @module components/premium/FeatureTeaser
 */

import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconSuccess, IconChevronRight } from '../../icons';
import Card from '../common/Card';
import Button from '../common/Button';
import Badge from '../common/Badge';
import { buildUpgradeHref, NudgePromotionId } from '../../config/nudgePromotions';
import analyticsService from '../../services/analyticsService';

/** One value-proposition bullet shown below the description. */
export interface FeatureTeaserHighlight {
  /** i18n key for the highlight label. */
  labelKey: string;
  /** Fallback text (react-i18next `defaultValue`) — German by convention. */
  defaultLabel: string;
}

export interface FeatureTeaserProps {
  /** Illustrative icon shown above the title (e.g. a react-icons component). */
  icon: ReactNode;
  /** i18n key + fallback for the eyebrow badge (e.g. "Premium-Funktion"). */
  badgeKey: string;
  badgeDefault: string;
  /** i18n key + fallback for the headline. */
  titleKey: string;
  titleDefault: string;
  /** i18n key + fallback for the explanatory paragraph. */
  descriptionKey: string;
  descriptionDefault: string;
  /** Optional bullet list of concrete feature value-props. */
  highlights?: FeatureTeaserHighlight[];
  /** i18n key + fallback for the CTA label. */
  ctaKey: string;
  ctaDefault: string;
  /**
   * Nudge-Promotion-ID (architecture-nudge.md §7.2). REQUIRED — der
   * Compiler erzwingt damit, dass jeder heutige und künftige Consumer für
   * Messung + Upgrade-Href verdrahtet ist (§13 Delta 1: ein optionales Prop
   * würde still unvermessene Teaser zulassen).
   */
  promotionId: NudgePromotionId;
  /**
   * GA4 `creative_slot` — unterscheidet mehrere Render-Stellen derselben
   * `promotionId` (z. B. `'inbox_stream'` vs. `'page'` für den
   * Belegscanner-Teaser, architecture-nudge.md §7.4). Optional.
   */
  creativeSlot?: string;
  /** i18n namespace(s) passed to `useTranslation`. Default `'common'`. */
  namespace?: string | string[];
  /**
   * `'page'` (default) — full-bleed centered card, the ENTIRE content a
   * caller renders in place of `return null` (matches the pre-existing
   * `ReceivedInvoicesPage` premium lock, `min-h-screen` flex-center).
   * Byte-identical to the pre-PR-T6-F1 markup when omitted.
   *
   * `'inline'` — compact horizontal card band (no `min-h-screen`), for
   * callers that only replace ONE section of an otherwise-visible page
   * (e.g. `ExpensesPage`'s `ExpenseSummaryBar` slot — the beleg list below
   * it must stay above the fold).
   */
  layout?: 'page' | 'inline';
}

/**
 * Full-bleed centered upsell card (default `layout="page"`). Intended to be
 * the ENTIRE content a caller renders in place of `return null` — matches
 * the layout of the existing `ReceivedInvoicesPage` premium lock
 * (`min-h-screen` flex-center). Pass `layout="inline"` for a compact
 * horizontal variant that fits into a partial-page slot instead.
 */
export function FeatureTeaser({
  icon,
  badgeKey,
  badgeDefault,
  titleKey,
  titleDefault,
  descriptionKey,
  descriptionDefault,
  highlights,
  ctaKey,
  ctaDefault,
  promotionId,
  creativeSlot,
  namespace = 'common',
  layout = 'page',
}: FeatureTeaserProps) {
  const { t } = useTranslation(namespace);
  const navigate = useNavigate();

  // Telemetrie (M-N5, architecture-nudge.md §7.4): `view_promotion` feuert
  // 1x pro Mount für JEDEN Consumer — Hook läuft unbedingt, kein
  // Early-Return davor (Rules of Hooks).
  useEffect(() => {
    analyticsService.trackViewPromotion({
      promotion_id: promotionId,
      ...(creativeSlot ? { creative_slot: creativeSlot } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionId, creativeSlot]);

  const handleCtaClick = () => {
    analyticsService.trackSelectPromotion({
      promotion_id: promotionId,
      ...(creativeSlot ? { creative_slot: creativeSlot } : {}),
    });
    // buildUpgradeHref(promotionId) ist die EINZIGE Href-Quelle (s. Modul-JSDoc):
    // trägt `?ref=` (M-N4-Attribution) UND `?tier=` (Spalten-Hervorhebung, D-1).
    navigate(buildUpgradeHref(promotionId));
  };

  if (layout === 'inline') {
    // Compact horizontal band — same content as the 'page' variant, no
    // min-h-screen. Fits into a partial-page slot (e.g. ExpensesPage's
    // ExpenseSummaryBar position) without pushing the page's primary
    // content (the beleg list) below the fold (PR-T6-F1, Reviewer-Fund F1).
    return (
      <Card className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="bg-[var(--tone-accent-bg)] rounded-full p-3 text-accent-ink text-xl flex-shrink-0">
              {icon}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge tone="info">{t(badgeKey, badgeDefault)}</Badge>
                <h2 className="text-base font-bold text-primary">{t(titleKey, titleDefault)}</h2>
              </div>
              <p className="text-sm text-secondary leading-snug">
                {t(descriptionKey, descriptionDefault)}
              </p>
              {highlights && highlights.length > 0 && (
                <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {highlights.map((highlight) => (
                    <li key={highlight.labelKey} className="flex items-center gap-1 text-xs text-secondary">
                      <IconSuccess size={12} className="flex-shrink-0 text-accent-ink" />
                      <span>{t(highlight.labelKey, highlight.defaultLabel)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <Button
            type="button"
            onClick={handleCtaClick}
            icon={<IconChevronRight size={14} />}
            className="flex-shrink-0 self-start sm:self-center"
          >
            {t(ctaKey, ctaDefault)}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <Card className="max-w-lg w-full text-center">
        <div className="flex justify-center mb-5">
          <div className="bg-[var(--tone-accent-bg)] rounded-full p-4 text-accent-ink text-3xl">
            {icon}
          </div>
        </div>

        <div className="flex justify-center mb-3">
          <Badge tone="info">{t(badgeKey, badgeDefault)}</Badge>
        </div>

        <h1 className="text-xl font-bold text-primary mb-3">{t(titleKey, titleDefault)}</h1>

        <p className="text-secondary mb-6 leading-relaxed">
          {t(descriptionKey, descriptionDefault)}
        </p>

        {highlights && highlights.length > 0 && (
          <ul className="text-left mb-6 space-y-2">
            {highlights.map((highlight) => (
              <li key={highlight.labelKey} className="flex items-start gap-2 text-sm text-secondary">
                <IconSuccess size={14} className="mt-0.5 flex-shrink-0 text-accent-ink" />
                <span>{t(highlight.labelKey, highlight.defaultLabel)}</span>
              </li>
            ))}
          </ul>
        )}

        <Button
          type="button"
          onClick={handleCtaClick}
          icon={<IconChevronRight size={14} />}
        >
          {t(ctaKey, ctaDefault)}
        </Button>
      </Card>
    </div>
  );
}

export default FeatureTeaser;
